import { DiagnosisCode } from 'src/contracts/diagnosisCode';
import { GrouperData } from 'src/contracts/grouperData';
import { PatientData } from 'src/contracts/patientData';
import {
  DiagnosisResultDto,
  GetGrouperDataResponse, GrouperInfoDto,
  GrouperPatientInfo,
  ProcedureResultDto
} from 'src/contracts/getGrouperDataResponse';
import { isEmpty } from 'src/common/arrayUtil';
import { GrouperInfo } from 'src/contracts/grouperInfo';
import { DrgAnalysisCode, DrgAnalysis, Requirement, Suggestion } from 'src/contracts/drgAnalysis';
import { AnalyseCode, AnalyseCodeList, AnalyseResponseRequirement, AnalyseResponseSuggestion, DrgAnalyseResponseDto } from 'src/contracts/drgAnalyseResponseDto';
import { Code } from 'src/contracts/code';
import { createDxCodeWithChipInfo } from 'src/common/code-helper';

export function convertGrouperData(response: GetGrouperDataResponse): GrouperData {
  return {
    grouperInfo: getGrouperInfo(response.grouper_info),
    drgInfo: response.drg_info || [],
    readOnly: response.read_only,
    patient: getPatient(response.patient),
    diagnosisCodes: getDiagnosisCodes(response.diagnoses, response.grouper_info?.grouper_source?.grouper_type),
    procedureCodes: getProcedureCodes(response.procedures),
    draft_data: response.draft_data
  };
}

function getGrouperInfo(grouperInfoDto: GrouperInfoDto): GrouperInfo {
  if (!grouperInfoDto) {
    throw new Error('GrouperInfo data not found');
  }

  return {
    medicare_provider_number: grouperInfoDto.medicare_provider_number,
    grouper_state: grouperInfoDto.grouper_state || [],
    grouper_source: grouperInfoDto.grouper_source
  }
}


function getPatient(patientData: GrouperPatientInfo): PatientData {
  if (!patientData) {
    throw new Error('Patient data not found');
  }

  return {
    name: patientData.patient_full_name,
    dischargeDate: patientData.discharge_date || 'none',
    sex: patientData.sex,
    codingSystem: patientData.coding_system,
    los: patientData.los
  }
}

function getProcedureCodes(procedures: ProcedureResultDto[]): Code[] {
  if (isEmpty(procedures)) {
    return [];
  }

  return procedures.map((procedure: ProcedureResultDto) => {
    return <Code>{
      id: procedure.position,
      code: procedure.code,
      description: procedure.description,
      drgModifier: procedure?.flags?.includes('OR') ? ['OR'] : []
    }
  })
}

function getDiagnosisCodes(diagnoses: DiagnosisResultDto[], grouperType: string): DiagnosisCode[] {
  if (isEmpty(diagnoses)) {
    return [];
  }
  return diagnoses.map((diagnosis: DiagnosisResultDto, index: number) => {
    return createDxCodeWithChipInfo(diagnosis.position, diagnosis.code, index == 0, diagnosis.description, diagnosis.poa?.trim(),
      diagnosis.flags?.includes('MCC'), diagnosis.flags?.includes('CC'), diagnosis.flags?.includes('HAC'),
      diagnosis.hcc, diagnosis.elixhauser_flags, diagnosis.soi_level, diagnosis.rom_level, grouperType === 'APR');
  })
}

function getDrgAnalysisSuggestions(suggestions: AnalyseResponseSuggestion[]): Suggestion[] {
  if (isEmpty(suggestions)) {
    return [];
  }

  return suggestions.map((suggestion: AnalyseResponseSuggestion) => {
    return <Suggestion>{
      drg: {
        suggestedDrg: suggestion.suggested_drg,
        weight: suggestion.drg_weight,
        description: suggestion.drg_description,
      },
      requirements: getDrgAnalyseRequirement(suggestion.requirements)
    }
  })
}

function getDrgAnalyseRequirement(requirements: AnalyseResponseRequirement[]): Requirement[] {
  if (isEmpty(requirements)) {
    return [];
  }

  return requirements.map((requirement: AnalyseResponseRequirement) => {
    return <Requirement>{
      description: requirement.description,
      codelist: getCodeList(requirement.codelists)
    }
  })
}

function getCodeList(codes: AnalyseCodeList[]): DrgAnalysisCode[] {
  if (isEmpty(codes)) {
    return [];
  }
  return codes.flatMap((codeList: AnalyseCodeList) => {
    return codeList.codelist
  }).map((code: AnalyseCode) => {
    return <DrgAnalysisCode>{
      codeType: code.code_type,
      value: code.value,
      shortDescription: code.short_description,
      mediumDescription: code.medium_description,
      effective_date: code.effective_date
    }
  })
}

export function convertDrgAnalyseData(response: DrgAnalyseResponseDto): DrgAnalysis {
  return {
    initialDrg: response.initial_drg,
    suggestions: getDrgAnalysisSuggestions(response.suggestions ?? []),
  };
}

export function convertCodes(codes: AnalyseCode[]): DrgAnalysisCode[] {
  return codes.map((code: AnalyseCode) => {
    return <DrgAnalysisCode>{
      codeType: code.code_type,
      value: code.value,
      shortDescription: code.short_description,
      mediumDescription: code.medium_description,
      effective_date: code.effective_date
    }
  })
}